import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Newspaper, CalendarDays, ArrowRight } from 'lucide-react';

const FONTS = {
  default: "'Inter', sans-serif",
  barlow: "'Barlow Condensed', sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'Courier New', monospace",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAG_STYLES = {
  cyan: 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300',
  green: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
  amber: 'bg-amber-500/15 border-amber-500/40 text-amber-300',
  red: 'bg-rose-500/15 border-rose-500/40 text-rose-300',
  purple: 'bg-purple-500/15 border-purple-500/40 text-purple-300',
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

export const CompanyNews = () => {
  const [news, setNews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/news`).then(res => setNews(res.data || [])).catch(() => {});
  }, []);

  if (news.length === 0) return null;

  return (
    <section className="py-20 md:py-24 bg-[#060a13] relative overflow-hidden" id="news" data-testid="company-news-section">
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 text-xs font-semibold mb-4">
            <Newspaper className="w-3.5 h-3.5" /> Лента подій
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Новини компанії</h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Актуальні події, акції та оновлення сервісу CargoAgs
          </p>
        </div>

        <div className="space-y-4">
          {news.map((item, i) => (
            <div
              key={item.id}
              className="group flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6 rounded-2xl border border-blue-500/20 bg-[#0a1224]/80 backdrop-blur p-5 sm:p-6 hover:border-cyan-400/50 hover:shadow-[0_0_25px_-10px_rgba(6,182,212,0.5)] transition-all"
              data-testid={`news-item-${item.id}`}
            >
              <div className="flex-shrink-0 sm:w-40">
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <CalendarDays className="w-4 h-4 text-cyan-400" />
                  <span className="tabular-nums">{formatDate(item.date_iso)}</span>
                </div>
                <span className={`inline-block mt-2 px-2.5 py-0.5 rounded-full border text-[11px] font-semibold ${TAG_STYLES[item.tag_color] || TAG_STYLES.cyan}`}>
                  {item.tag}
                </span>
              </div>
              <div className="min-w-0 flex-1" style={{ fontFamily: FONTS[item.font] || FONTS.default }}>
                <h3 className="text-white font-semibold text-lg group-hover:text-cyan-300 transition-colors">{item.title}</h3>
                {item.text && <p className="text-slate-400 text-sm mt-1.5 leading-relaxed whitespace-pre-line">{item.text}</p>}
                {item.link_url && (
                  <a
                    href={item.link_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors"
                    data-testid={`news-link-${item.id}`}
                  >
                    {item.link_label || 'Детальніше'} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
              {item.image && (
                <div className="flex-shrink-0 sm:w-44 w-full">
                  <img src={item.image} alt={item.title} className="w-full h-32 sm:h-28 object-cover rounded-xl border border-blue-500/20" loading="lazy" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
